import { useFlags } from 'launchdarkly-react-client-sdk';

import { useFavoriteResourceUuidsQuery } from 'data/queries/useFavoriteResourceUuidsQuery.hook';
import { useSearchParams } from 'hooks/use-search-params.hook';
import { searchParams as SearchParams } from 'shared/types/search-params';

interface UseFavoritesHighlightValues {
  /** The favorites flag is on: the star may be drawn on the details card. */
  isFavoritesEnabled: boolean;
  /** The user's favorites, whether or not the highlight is on — the details star reads it. */
  favoriteResourceUuids: ReadonlySet<string>;
  /** The favorites are known (loaded). Until then the star waits, and nothing is highlighted. */
  isFavoritesLoaded: boolean;
  /**
   * The favorites to light up on the map, or null while the highlight is off. The filter bar's star
   * writes `favoritesOnly` to the url — on the list that narrows the resources, on the map it turns
   * this highlight on instead. Null as well until the favorites have loaded: an empty set then
   * means "unknown", and highlighting it would dim the whole floor.
   */
  highlightedFavoriteUuids: ReadonlySet<string> | null;
}

/** What the map needs to know about favorites: the user's set and whether to highlight it. */
export const useFavoritesHighlight = (): UseFavoritesHighlightValues => {
  const { enableFavoriteResourceBookings } = useFlags();
  const { favoritesOnly } = useSearchParams<SearchParams>();
  const isFavoritesEnabled = !!enableFavoriteResourceBookings;
  const { uuids: favoriteResourceUuids, isLoaded } = useFavoriteResourceUuidsQuery({ enabled: isFavoritesEnabled });
  const isFavoritesLoaded = isFavoritesEnabled && isLoaded;

  return {
    isFavoritesEnabled,
    favoriteResourceUuids,
    isFavoritesLoaded,
    highlightedFavoriteUuids: isFavoritesLoaded && favoritesOnly === 'true' ? favoriteResourceUuids : null,
  };
};
